import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Settings,
  Key,
  Check,
  X,
  Zap,
  Eye,
  EyeOff,
  ExternalLink,
  TestTube,
  Loader2,
  Mic,
  Video,
  Image as ImageIcon,
  Scissors,
  Bot,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  Link2,
  Shield,
  Wand2
} from 'lucide-react';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';

// API Service configurations
const API_SERVICES = [
  {
    id: 'openrouter',
    name: 'OpenRouter',
    description: 'Motor de IA para generación de copy (Claude, GPT-4)',
    icon: Bot,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
    keyPrefix: 'sk-or-',
    docsUrl: 'https://openrouter.ai/keys',
    category: 'ai'
  },
  {
    id: 'elevenlabs',
    name: 'ElevenLabs',
    description: 'Síntesis de voz y clonación para videos',
    icon: Mic,
    color: 'text-pink-400',
    bgColor: 'bg-pink-500/10',
    borderColor: 'border-pink-500/30',
    keyPrefix: '',
    docsUrl: 'https://elevenlabs.io/app/speech-synthesis',
    category: 'voice'
  },
  {
    id: 'heygen',
    name: 'HeyGen',
    description: 'Avatares con lip-sync para videos de expertos',
    icon: Video,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    keyPrefix: '',
    docsUrl: 'https://app.heygen.com/settings/api',
    category: 'video'
  },
  {
    id: 'creatomate',
    name: 'Creatomate',
    description: 'Renderizado de videos con subtítulos dinámicos',
    icon: Video,
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    keyPrefix: '',
    docsUrl: 'https://app.creatomate.com/settings/api',
    category: 'video'
  },
  {
    id: 'removebg',
    name: 'Remove.bg',
    description: 'Eliminación de fondos de imágenes',
    icon: Scissors,
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30',
    keyPrefix: '',
    docsUrl: 'https://www.remove.bg/dashboard#api-key',
    category: 'image'
  },
  {
    id: 'freepik',
    name: 'Freepik AI',
    description: 'Generación de imágenes y búsqueda de fondos',
    icon: ImageIcon,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/10',
    borderColor: 'border-cyan-500/30',
    keyPrefix: '',
    docsUrl: 'https://www.freepik.com/developers/dashboard',
    category: 'image'
  },
  {
    id: 'google_gemini',
    name: 'Google Gemini',
    description: 'Nano Banana y Gemini TTS — imágenes y voz económicas',
    icon: Sparkles,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30',
    keyPrefix: '',
    docsUrl: 'https://aistudio.google.com/apikey',
    category: 'image'
  },
  {
    id: 'fal_ai',
    name: 'fal.ai (Kling)',
    description: 'Generación de videos con Kling AI y avatares lip-sync',
    icon: Video,
    color: 'text-rose-400',
    bgColor: 'bg-rose-500/10',
    borderColor: 'border-rose-500/30',
    keyPrefix: '',
    docsUrl: 'https://fal.ai/dashboard/keys',
    category: 'video'
  },
  {
    id: 'fal_ai_flux',
    name: 'fal.ai (Flux Pro)',
    description: 'Generación de imágenes fotorrealistas con Flux Pro',
    icon: ImageIcon,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    keyPrefix: '',
    docsUrl: 'https://fal.ai/dashboard/keys',
    category: 'image',
    sharedKey: 'fal_ai' // Uses same key as fal_ai
  }
];

const CATEGORIES = [
  { id: 'all', name: 'Todas', icon: Sparkles },
  { id: 'ai', name: 'IA & Copy', icon: Bot },
  { id: 'voice', name: 'Voz', icon: Mic },
  { id: 'video', name: 'Video', icon: Video },
  { id: 'image', name: 'Imagen', icon: ImageIcon }
];

// Single API Key Card Component
const APIKeyCard = ({ service, config, onSave, onTest }) => {
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  
  const IconComponent = service.icon;
  // For shared keys, check the parent key's configuration
  const effectiveId = service.sharedKey || service.id;
  const isConfigured = config?.[`has_${effectiveId}_key`] || false;
  const testResult = config?.[`${effectiveId}_test_result`];
  const isSharedKey = !!service.sharedKey;

  const handleSave = async () => {
    if (!apiKey.trim()) {
      toast.error('Por favor ingresa una API Key');
      return;
    }
    setSaving(true);
    try {
      await onSave(effectiveId, apiKey);
      setApiKey('');
      toast.success(`${service.name} configurado correctamente`);
    } catch (error) {
      toast.error(`Error al guardar: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const result = await onTest(effectiveId);
      if (result.success) {
        toast.success(`${service.name}: Conexión exitosa`);
      } else {
        toast.error(`${service.name}: ${result.error}`);
      }
    } catch (error) {
      toast.error(`Error al probar: ${error.message}`);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div 
      className={`bg-surface border rounded-xl p-5 transition-all hover:border-brand/30 ${
        isConfigured ? service.borderColor : 'border-border-subtle'
      }`}
      data-testid={`api-card-${service.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${service.bgColor}`}>
            <IconComponent className={`w-5 h-5 ${service.color}`} />
          </div>
          <div>
            <h3 className="font-medium text-text-main">{service.name}</h3>
            <p className="text-xs text-text-muted">{service.description}</p>
            {isSharedKey && (
              <p className="text-xs text-brand/70 mt-0.5">Usa la misma key que fal.ai (Kling)</p>
            )}
          </div>
        </div>
        
        {/* Status Badge */}
        <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${
          isConfigured 
            ? 'bg-green-500/10 text-green-400' 
            : 'bg-yellow-500/10 text-yellow-400'
        }`}>
          {isConfigured ? (
            <>
              <CheckCircle2 className="w-3 h-3" />
              Configurado
            </>
          ) : (
            <>
              <AlertCircle className="w-3 h-3" />
              Sin configurar
            </>
          )}
        </div>
      </div>

      {/* API Key Input - Hide for shared keys if already configured */}
      {isSharedKey && isConfigured ? (
        <div className="text-sm text-text-muted bg-app-bg/50 rounded-lg p-3 text-center">
          <CheckCircle2 className="w-4 h-4 inline mr-2 text-green-400" />
          Key compartida con fal.ai (Kling) - Ya configurada
        </div>
      ) : (
        <div className="space-y-3">
          <div className="relative">
            <Input
              type={showKey ? 'text' : 'password'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={isConfigured ? '••••••••••••••••' : `Ingresa tu API Key de ${service.name}`}
              className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted pr-10 font-mono text-sm"
              data-testid={`${service.id}-api-key-input`}
            />
            <button
              type="button"
              onClick={() => setShowKey(!showKey)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-main"
            >
              {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || !apiKey.trim()}
            className="bg-brand-500 hover:bg-brand-hover text-white flex-1"
            data-testid={`save-${service.id}-btn`}
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Key className="w-4 h-4 mr-2" />
            )}
            Guardar
          </Button>
          
          {isConfigured && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleTest}
              disabled={testing}
              className="border-border-subtle text-text-secondary hover:text-text-main"
              data-testid={`test-${service.id}-btn`}
            >
              {testing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <TestTube className="w-4 h-4" />
              )}
            </Button>
          )}
          
          <a
            href={service.docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-text-muted hover:text-brand-500 transition-colors"
            title={`Obtener API Key de ${service.name}`}
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
      )}

      {/* Test Result */}
      {testResult && (
        <div className={`mt-3 p-2 rounded-lg text-xs ${
          testResult.success 
            ? 'bg-green-500/10 text-green-400' 
            : 'bg-red-500/10 text-red-400'
        }`}>
          {testResult.success ? (
            <div className="flex items-center gap-2">
              <Check className="w-3 h-3" />
              <span>Última prueba exitosa: {testResult.message}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <X className="w-3 h-3" />
              <span>Error: {testResult.error}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Main Component
const AdminAPIConfig = () => {
  const { api, user } = useAuth();
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  const isAuthorized = user?.role === 'super_admin';

  const fetchConfig = async () => {
    try {
      const response = await api.get('/admin/api-keys');
      setConfig(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Acceso denegado');
      } else {
        toast.error('Error al cargar la configuración');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthorized) {
      fetchConfig();
    } else {
      setLoading(false);
    }
  }, [isAuthorized]);

  if (!isAuthorized && !loading) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSaveKey = async (serviceId, apiKey) => {
    await api.put('/admin/api-keys', {
      service_id: serviceId,
      api_key: apiKey
    });
    await fetchConfig();
  };

  const handleTestKey = async (serviceId) => {
    const response = await api.post(`/admin/api-keys/${serviceId}/test`);
    await fetchConfig();
    return response.data;
  };

  // Filter services by category
  const filteredServices = activeCategory === 'all' 
    ? API_SERVICES 
    : API_SERVICES.filter(s => s.category === activeCategory);

  // Count configured services
  const configuredCount = API_SERVICES.filter(s => config[`has_${s.id}_key`]).length;

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-6xl space-y-6" data-testid="admin-api-config-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand to-purple-600 flex items-center justify-center">
              <Link2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-text-main">Configuración de APIs</h1>
              <p className="text-text-secondary text-sm">
                Conecta los servicios externos para generación de contenido
              </p>
            </div>
          </div>
          
          {/* Global Status */}
          <div className="flex items-center gap-2 px-4 py-2 bg-surface border border-border-subtle rounded-xl">
            <Shield className={`w-5 h-5 ${configuredCount === API_SERVICES.length ? 'text-green-400' : 'text-yellow-400'}`} />
            <span className="text-sm text-text-main">
              <span className="font-bold">{configuredCount}</span>
              <span className="text-text-muted">/{API_SERVICES.length} servicios</span>
            </span>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 p-1 bg-app-bg rounded-xl border border-border-subtle overflow-x-auto">
          {CATEGORIES.map((cat) => {
            const CatIcon = cat.icon;
            const count = cat.id === 'all' 
              ? API_SERVICES.length 
              : API_SERVICES.filter(s => s.category === cat.id).length;
            
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all whitespace-nowrap ${
                  activeCategory === cat.id 
                    ? 'bg-surface border border-border-subtle shadow-lg text-brand' 
                    : 'hover:bg-surface/50 text-text-secondary'
                }`}
              >
                <CatIcon className="w-4 h-4" />
                <span className="text-sm font-medium">{cat.name}</span>
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                  activeCategory === cat.id ? 'bg-brand/20 text-brand' : 'bg-app-bg text-text-muted'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Important Notice */}
        <div className="bg-gradient-to-r from-purple-500/5 to-pink-500/5 border border-purple-500/20 rounded-xl p-4">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center shrink-0">
              <Zap className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h4 className="font-medium text-text-main mb-1">Director de Arte con IA</h4>
              <p className="text-sm text-text-secondary">
                Una vez configuradas las APIs, el sistema actuará como un <span className="text-purple-400 font-medium">Director de Arte</span> inteligente. 
                Analizará tu nicho y avatar para generar prompts de búsqueda precisos, 
                instrucciones de edición personalizadas y subtítulos dinámicos con tu branding.
              </p>
            </div>
          </div>
        </div>

        {/* API Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredServices.map((service) => (
            <APIKeyCard
              key={service.id}
              service={service}
              config={config}
              onSave={handleSaveKey}
              onTest={handleTestKey}
            />
          ))}
        </div>

        {/* Quick Links */}
        <div className="bg-surface border border-border-subtle rounded-xl p-4">
          <h3 className="font-medium text-text-main mb-3">Enlaces rápidos para obtener API Keys</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {API_SERVICES.map((service) => {
              const IconComponent = service.icon;
              return (
                <button
                  key={service.id}
                  onClick={() => window.open(service.docsUrl, '_blank', 'noopener,noreferrer')}
                  className={`flex items-center gap-2 p-2 rounded-lg border transition-all hover:border-brand/30 cursor-pointer ${
                    config[`has_${service.id}_key`] 
                      ? `${service.bgColor} ${service.borderColor}` 
                      : 'bg-app-bg border-border-subtle'
                  }`}
                >
                  <IconComponent className={`w-4 h-4 ${service.color}`} />
                  <span className="text-xs text-text-main truncate">{service.name}</span>
                  <ExternalLink className="w-3 h-3 text-text-muted ml-auto" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AdminAPIConfig;
